"use client"

import { ArrowRight, CheckCircle2, TriangleAlert } from 'lucide-react'
import { usePathname } from 'next/navigation'

import { DIAGNOSIS_TYPE_TO_SERVICE, getAngiAffiliateUrl } from '@/data/angi-affiliate'
import { trackEvent } from '@/lib/analytics'
import { useServiceLeadCtaViewed } from '@/hooks/use-service-lead-cta-viewed'
import type { DiagnosisIdentification } from '@/lib/types/diagnosis'

interface DiagnosisProReferralCardProps {
  problem: DiagnosisIdentification
  entryPath?: string
}

const SERVICE_NOUN: Record<DiagnosisIdentification['type'], string> = {
  pest: 'pest control pro',
  disease: 'lawn care pro',
  weed: 'weed control pro',
  nutrient_deficiency: 'lawn care pro',
  environmental: 'lawn care pro',
}

/**
 * Severity-gated Angi referral on the diagnose result screen. Only the caller
 * renders it (severe results); the framing is "have a pro handle it", right
 * after the immediate-actions checklist. Matches the result screen's premium
 * card chrome, not the landing-page or glassy in-content styles.
 */
export function DiagnosisProReferralCard({ problem, entryPath }: DiagnosisProReferralCardProps) {
  const service = DIAGNOSIS_TYPE_TO_SERVICE[problem.type] ?? 'lawn_care'
  const noun = SERVICE_NOUN[problem.type] ?? 'lawn care pro'
  const sid = entryPath === 'saved_result' ? 'diagnose_saved_result' : 'diagnose_result'
  const pathname = usePathname()
  // /result/[id] ends in a record id, not a page slug; the placement already
  // names that surface.
  const pageSlug = entryPath === 'saved_result' ? undefined : pathname === '/' ? 'home' : pathname ?? undefined
  const angiUrl = getAngiAffiliateUrl(service, sid, pageSlug)
  const headline = problem.name
    ? `${problem.name} is heavy work to fix alone`
    : 'This one is heavy work to fix alone'
  // Honest eyebrow per gate tier: severe problems, or moderate ones the model
  // flagged as needing immediate action.
  const isSevere = problem.severity === 'severe'
  const eyebrow = isSevere ? 'Severe issue found' : 'Needs action now'

  const viewRef = useServiceLeadCtaViewed({
    service_type: service,
    source_placement: sid,
    problem_type: problem.type,
  })

  return (
    <div
      ref={viewRef}
      className="mx-auto max-w-2xl rounded-2xl border border-green-100/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,16,0.04),0_8px_24px_-12px_rgba(22,101,52,0.12)] sm:p-6"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-amber-700">
        <TriangleAlert className="h-3.5 w-3.5" aria-hidden="true" />
        {eyebrow}
      </p>
      <h2 className="mt-2 text-lg font-semibold text-gray-950">{headline}</h2>
      <p className="mt-2 text-sm leading-6 text-gray-600">
        {isSevere
          ? `For damage at this level, a local ${noun} can treat it faster than DIY.`
          : `Caught in time, a local ${noun} can knock this out before it spreads.`}{' '}
        Free quotes through our partner Angi, no obligation.
      </p>
      <a
        href={angiUrl}
        target="_blank"
        rel="sponsored noopener noreferrer"
        className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-green-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-800 active:scale-[0.98] sm:w-auto"
        onClick={() =>
          trackEvent('service_lead_angi_clicked', {
            service_type: service,
            source_placement: sid,
            placement: sid,
            problem_type: problem.type,
          })
        }
      >
        Get matched with {noun}s
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
      <p className="mt-3 flex items-center gap-1.5 text-xs leading-5 text-gray-600">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" aria-hidden="true" />
        Local, background-checked pros, quotes usually within a day.
      </p>
      <p className="mt-2 text-xs leading-5 text-gray-500">
        Angi is a partner; we may earn a commission if you request quotes there.
      </p>
    </div>
  )
}
